import {
  BindingCandidate,
  BindingDetailsMap,
  BindingProperties,
  findBindingCandidatesOptions,
  ListBindingsOptions,
  UnbindAll,
  SelectBindingOptions,
} from '@wix/public-editor-platform-interfaces';
import { PrivateAPINamespace } from '../PrivateAPINamespace';
import {
  createEditorPlatformPrivateAPIError,
  EditorPlatformPrivateAPIErrorCode,
} from '../../errors';
import type {
  DataItem,
  FunctionParameter,
} from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';

export class BindingsNamespace extends PrivateAPINamespace {
  static readonly namespace = 'bindings';

  /**
   * @deprecated Use {@link setBinding} instead, which takes a flat
   * property-name -> binding dictionary with provider details inlined.
   */
  async bind(
    virtualManifestId: string,
    childPath: number[],
    bindings: BindingProperties[],
  ): Promise<void> {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = await api.query.bind(bindings);
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.BindError,
        error instanceof Error ? error.message : 'bind operation failed',
      );
    }
  }

  async setBinding(
    virtualManifestId: string,
    childPath: number[],
    bindings: BindingDetailsMap,
  ): Promise<void> {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = await api.query.setBinding(bindings);
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.SetBindingError,
        error instanceof Error ? error.message : 'set binding operation failed',
      );
    }
  }

  async unbind(
    virtualManifestId: string,
    childPath: number[],
    props: string[] | UnbindAll,
  ): Promise<void> {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = await api.query.unbind(props);
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.UnbindError,
        error instanceof Error ? error.message : 'unbind operation failed',
      );
    }
  }

  /**
   * @deprecated Use {@link getBinding} instead, which returns a flat
   * property-name -> binding dictionary with provider details inlined.
   */
  listBindings(
    virtualManifestId: string,
    childPath: number[],
    options?: ListBindingsOptions,
  ): BindingProperties[] {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = api.query.listBindings(options);
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.ListBindingsError,
        error instanceof Error
          ? error.message
          : 'list bindings operation failed',
      );
    }
  }

  getBinding(
    virtualManifestId: string,
    childPath: number[],
    options?: ListBindingsOptions,
  ): BindingDetailsMap {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = api.query.getBinding(options);
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.GetBindingError,
        error instanceof Error ? error.message : 'get binding operation failed',
      );
    }
  }

  findBindingCandidates(
    virtualManifestId: string,
    childPath: number[],
    bindingTargetDetails: DataItem | FunctionParameter,
    options?: findBindingCandidatesOptions,
  ): BindingCandidate[] {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const result = api.query.findBindingCandidates(
        bindingTargetDetails,
        options,
      );
      if (result instanceof Error) {
        throw result;
      }
      return result;
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.FindBindingCandidatesError,
        error instanceof Error
          ? error.message
          : 'find binding candidates operation failed',
      );
    }
  }

  #resolveOverriddenSelectBindingOptions(
    virtualManifestId: string,
    options: SelectBindingOptions,
  ) {
    const metadata =
      this.ps.manifestServicesHost.getContextualMetadata(virtualManifestId);
    const overridden = metadata?.overriddenSelectBindingOptions;
    const resolved =
      typeof overridden === 'function' ? overridden(options) : overridden;
    return resolved ?? {};
  }

  async selectBinding(
    virtualManifestId: string,
    childPath: number[],
    options: SelectBindingOptions,
  ): Promise<void> {
    try {
      if (
        options.maxBindingDepth !== undefined &&
        options.maxBindingDepth > 2
      ) {
        throw new Error('maxBindingDepth cannot exceed 2');
      }

      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const metadata =
        this.ps.manifestServicesHost.getContextualMetadata(virtualManifestId);
      const target = metadata?.boundObjectRef ?? {
        editorPointer: api.getEditorPointer(),
      };

      const overriddenOptions = this.#resolveOverriddenSelectBindingOptions(
        virtualManifestId,
        options,
      );
      const finalOptions = { ...options, ...overriddenOptions };

      if (this.ps.hostAPI.editor.selectBinding) {
        await this.ps.hostAPI.editor.selectBinding(target, finalOptions);
        return;
      }

      const selectBindingPanelUIService =
        this.ps.platformPackagesBridge?.SelectBindingPanelUIServiceAPI;

      if (selectBindingPanelUIService) {
        selectBindingPanelUIService.openPanel({ target, ...finalOptions });
        return;
      }

      throw new Error('selectBinding panel is not supported');
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.SelectBindingError,
        error instanceof Error
          ? error.message
          : 'select binding operation failed',
      );
    }
  }
}
